import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("sq-AL") : "—";

export default function Treatments() {
  const { format } = useCurrency();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [editing, setEditing] = useState(null);

  const canEdit = ["admin", "doctor"].includes(user?.role);

  const load = async () => {
    const [t, p] = await Promise.all([api.get("/treatments"), api.get("/patients")]);
    setList(t.data);
    setPatients(p.data);
  };

  useEffect(() => { load(); }, []);

  const patientName = (id) => {
    const p = patients.find((x) => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : "—";
  };

  const remove = async (t) => {
    if (!window.confirm(`Fshini trajtimin "${t.name}" për ${patientName(t.patient_id)}?\n\nKy veprim s'mund të kthehet.`)) return;
    try {
      await api.delete(`/treatments/${t.id}`);
      toast.success("Trajtimi u fshi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gabim gjatë fshirjes");
    }
  };

  return (
    <div className="space-y-8" data-testid="treatments-page">
      <div>
        <div className="label-eyebrow">Procedurat Klinike</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Trajtimet</h1>
        <p className="mt-2 text-sm text-[#5C5C5C]">Historiku i të gjitha procedurave të kryera në klinikë. {canEdit && "Klikoni ✏ për të modifikuar ose 🗑 për të fshirë."}</p>
      </div>

      <div className="surface-card overflow-hidden">
        {list.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">
            Pa trajtime. Shtojini nga dosja e pacientit ose nga odontograma.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <Th>Pacienti</Th>
                <Th>Trajtimi</Th>
                <Th>Dhëmbi</Th>
                <Th>Mjeku</Th>
                <Th>Data</Th>
                <Th className="text-right">Kosto</Th>
                {canEdit && <Th className="text-right">Veprime</Th>}
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className="border-t border-[#E2DFD8]" data-testid={`treatment-row-${t.id}`}>
                  <Td>{patientName(t.patient_id)}</Td>
                  <Td>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-[#5C5C5C]">{t.description || "—"}</div>
                  </Td>
                  <Td>{t.tooth_number || "—"}</Td>
                  <Td>{t.performed_by || "—"}</Td>
                  <Td>{fmt(t.performed_at)}</Td>
                  <Td className="text-right font-mono">{format(t.cost)}</Td>
                  {canEdit && (
                    <Td className="text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setEditing(t)}
                          className="p-1.5 rounded hover:bg-[#EAE4D9] text-[#2C4C3B]"
                          title="Edito trajtimin"
                          data-testid={`edit-treatment-${t.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => remove(t)}
                          className="p-1.5 rounded hover:bg-[#9E3C3C]/10 text-[#9E3C3C]"
                          title="Fshi trajtimin"
                          data-testid={`delete-treatment-${t.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <EditTreatmentDialog
          treatment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function EditTreatmentDialog({ treatment, onClose, onSaved }) {
  const [form, setForm] = useState({
    patient_id: treatment.patient_id,
    tooth_number: treatment.tooth_number || "",
    name: treatment.name || "",
    description: treatment.description || "",
    category: treatment.category || "",
    filling_class: treatment.filling_class || "",
    post_endo: !!treatment.post_endo,
    cost: treatment.cost || 0,
    surfaces: treatment.surfaces || [],
    performed_at: treatment.performed_at ? treatment.performed_at.slice(0, 16) : "",
    performed_by: treatment.performed_by || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost: Number(form.cost) || 0,
        performed_at: form.performed_at ? new Date(form.performed_at).toISOString() : null,
      };
      await api.put(`/treatments/${treatment.id}`, payload);
      toast.success("Trajtimi u përditësua");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Gabim gjatë përditësimit");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edito Trajtimin</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="edit-treatment-form">
          <div>
            <Label className="label-eyebrow">Emri / Procedura</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="edit-tx-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Dhëmbi (FDI)</Label>
              <Input value={form.tooth_number} onChange={(e) => setForm({ ...form, tooth_number: e.target.value })} placeholder="p.sh. 16" className="mt-2" data-testid="edit-tx-tooth" />
            </div>
            <div>
              <Label className="label-eyebrow">Kategoria</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Mbushje / Kanal / Kurorë" className="mt-2" data-testid="edit-tx-category" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow">Kosto (ALL)</Label>
              <Input type="number" step="100" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="mt-2" data-testid="edit-tx-cost" />
            </div>
            <div>
              <Label className="label-eyebrow">Data e Kryerjes</Label>
              <Input type="datetime-local" value={form.performed_at} onChange={(e) => setForm({ ...form, performed_at: e.target.value })} className="mt-2" data-testid="edit-tx-date" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Mjeku</Label>
            <Input value={form.performed_by} onChange={(e) => setForm({ ...form, performed_by: e.target.value })} className="mt-2" data-testid="edit-tx-doctor" />
          </div>
          <div>
            <Label className="label-eyebrow">Përshkrimi / Shënime</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" data-testid="edit-tx-desc" />
          </div>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={onClose}>Anulo</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid="edit-tx-save">
              {saving ? "Duke ruajtur..." : "Ruaj Ndryshimet"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-6 py-3 label-eyebrow ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-6 py-4 align-top ${className}`}>{children}</td>
);
